import {Injectable} from "@angular/core";
import {UploadRepository} from "../repository/upload.repository";
import {BehaviorSubject, catchError, filter, Observable, of, switchMap, tap} from "rxjs";
import {HttpEventType} from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class UploadService {

  private _progressUpload = new BehaviorSubject<number>(100);

  constructor(private _uploadRepo: UploadRepository) {
    this.progressUpload = 100
  }

  get progressUpload(): Observable<number> {
    return this._progressUpload
  }

  set progressUpload(value: number) {
    this._progressUpload.next(value)
  }

  upload(companyCode: string, formData: FormData) {
    return this._uploadRepo.upload(companyCode, formData).pipe(
      tap((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.progressUpload = Math.round(100 * event.loaded / event.total);
        }
      }),
      filter((event) => event.type === HttpEventType.Response),
      switchMap((event) => {
        this.progressUpload = 100
        if (event.type === HttpEventType.Response) {
          return of(event.body.result)
        }
        return of([])
      }),
      catchError((err) => {
        this.progressUpload = 100
        throw err
      }),
    )
  }

  delete(companyCode: string, filename: string) {
    return this._uploadRepo.delete(companyCode, filename);
  }

  async compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not create canvas context'));
            return;
          }
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
            const compressedFile = new File([blob], file.name, {type: 'image/jpeg', lastModified: Date.now()});
            resolve(compressedFile);
          }, 'image/jpeg', 0.7); // Image quality (0.7 means 70%)
        }
      };

      reader.readAsDataURL(file);
    });
  }
}
